"use client";

import { ReactLenis, useLenis } from "lenis/react";
import { usePathname } from "next/navigation";
import { ReactNode, useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { registerLenis, scrollToSection } from "@/lib/utils/scroll";

if (typeof window !== "undefined") gsap.registerPlugin(ScrollTrigger);

const ScrollProvider = ({ children }: { children: ReactNode }) => {
  const pathName = usePathname();
  const lenis = useLenis();

  // Lenis drives scrolling on its own rAF loop, so ScrollTrigger never hears the
  // native scroll events it relies on — pinning/scrub tweens then update late or
  // only fire once. Feeding every lenis scroll into ScrollTrigger.update() keeps
  // all scroll animations in sync and, crucially, reversible on scroll-up.
  // Hand the instance to the module so consumers mounted outside this provider
  // (the booking provider, the floating bar) scroll through lenis too.
  useEffect(() => {
    registerLenis(lenis ?? null);
    return () => registerLenis(null);
  }, [lenis]);

  useEffect(() => {
    if (!lenis) return;
    const onScroll = () => ScrollTrigger.update();
    lenis.on("scroll", onScroll);
    ScrollTrigger.refresh();
    return () => {
      lenis.off("scroll", onScroll);
    };
  }, [lenis]);

  useEffect(() => {
    if (!lenis) return;

    // If the destination URL carries a hash (e.g. /about-us#team), scroll to
    // that section instead of the top. The target may mount a beat late (page
    // transition + GSAP layout), so retry until it exists.
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      let tries = 0;
      let timer = 0;
      const tryScroll = () => {
        if (scrollToSection(id)) return;
        if (tries++ < 20) timer = window.setTimeout(tryScroll, 150);
      };
      timer = window.setTimeout(tryScroll, 150);
      return () => clearTimeout(timer);
    }

    // Any hash-less navigation starts at the top — including one back to the
    // home page, which the old `!== "/"` guard skipped and left mid-page.
    lenis.scrollTo(0, { immediate: true });
  }, [pathName, lenis]);
  return (
    <ReactLenis
      root
      options={{
        duration: 1.5,
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 2,
        infinite: false,
      }}
    >
      {children}
    </ReactLenis>
  );
};

export default ScrollProvider;
